<script setup lang="ts">
import type { Configurator } from '~/models/configurator/configurator.model'
import type { ConfiguratorPool } from '~/models/configurator/configuratorPool.model'
import type { ConfiguratorPoolColor } from '~/models/configurator/configuratorPoolColor.model'

import type { ConfiguratorPoolLine } from '~/models/configurator/configuratorPoolLine.model'
import type { ConfiguratorPoolSize } from '~/models/configurator/configuratorPoolSize.model'
import type { ConfiguratorPoolStair } from '~/models/configurator/configuratorPoolStair.model'
import { useConfigurator } from '~/composables/configurator/useConfigurator'
import { useConfiguratorData } from '~/composables/configurator/useConfiguratorData'
import ConfiguratorCarousel from './ConfiguratorCarousel.vue'

interface Props {
  configurator: Configurator
  isFullyFilledIn: boolean
}

const props = defineProps<Props>()

const { dt } = useDirectusTranslation()

const configuratorCarouselElement = ref<InstanceType<typeof ConfiguratorCarousel> | null>(null)

const {
  allColors,
  allPoolLines,
  allSizes,
  allStairs,
} = useConfiguratorData({
  configurator: computed(() => props.configurator),
})

const currentPools = computed<ConfiguratorPool[]>(() => {
  return props.configurator.pools
})

const configurator = computed(() => {
  return props.configurator
})
const route = useRoute()
const router = useRouter()

const poolLineId = computed(() => route.query.poolLine)
const stairId = computed(() => route.query.stair)
const colorId = computed(() => route.query.color)
const sizeId = computed(() => route.query.size)

const currentPoolLine = computed<ConfiguratorPoolLine>({
  get: () => {
    return allPoolLines.value.find(poolLine => poolLine.id === Number(poolLineId.value)) ?? currentPools.value[0].poolLine
  },
  set: (value) => {
    updateQuery('poolLine', String(value?.id))
  },
})

const currentStair = computed<ConfiguratorPoolStair>({
  get: () => {
    return allStairs.value.find(stair => stair.id === Number(stairId.value)) ?? currentPools.value[0].stair
  },
  set: (value) => {
    updateQuery('stair', String(value?.id))
  },
})

const currentColor = computed<ConfiguratorPoolColor>({
  get: () => {
    return allColors.value.find(color => color.id === Number(colorId.value)) ?? currentPools.value[0].color
  },
  set: (value) => {
    updateQuery('color', String(value?.id))
  },
})

const currentSize = computed<ConfiguratorPoolSize>({
  get: () => {
    return allSizes.value.find(size => size.id === Number(sizeId.value)) ?? currentPools.value[0].size
  },
  set: (value) => {
    updateQuery('size', String(value?.id))
  },
})

const {
  currentPool,
  currentSelectableSizes,
  currentShownSizes,
  poolLines,
  currentShownColors,
  currentSelectableColors,
  currentSelectableStairs,
  currentShownStairs,
  getPossiblePoolOptions,
} = useConfigurator({
  configurator,
  currentStair,
  currentColor,
  currentSize,
  currentPoolLine,
})

function updateQuery(key: string, value: string) {
  // This is needed to keep the query in sync for some reason please dont remove
  setTimeout(() => {
    router.replace(
      {
        query: {
          poolLine: poolLineId.value ?? poolLines.value[0].id,
          stair: String(currentStair.value.id) ?? currentShownStairs.value[0].id,
          color: String(currentColor.value.id) ?? currentShownColors.value[0].id,
          size: String(currentSize.value.id) ?? currentShownSizes.value[0].id,
          [key]: value,
        },
      },
    )
  }, 1)
}

watch(() => currentPoolLine.value.id, () => {
  router.replace({
    query: {
      poolLine: String(currentPoolLine.value.id),
      stair: String(currentShownStairs.value[0].id),
      color: String(currentShownColors.value[0].id),
      size: String(currentShownSizes.value[0].id),
    },
  })
}, {
  deep: true,
})

async function checkParamsArePossible() {
  const isStairPossible = getPossiblePoolOptions({
    poolLine: currentPoolLine.value,
    stair: currentStair.value,
    color: null,
    size: null,
  }).length > 0

  const isColorPossible = getPossiblePoolOptions({
    poolLine: currentPoolLine.value,
    stair: currentStair.value,
    color: currentColor.value,
    size: null,
  }).length > 0

  const isSizePossible = getPossiblePoolOptions({
    poolLine: currentPoolLine.value,
    stair: currentStair.value,
    color: currentColor.value,
    size: currentSize.value,
  }).length > 0

  if (!isStairPossible)
    currentStair.value = currentShownStairs.value[0]

  if (!isColorPossible)
    currentColor.value = currentShownColors.value[0]

  if (!isSizePossible)
    currentSize.value = currentShownSizes.value[0]
}

checkParamsArePossible()

function onSelectPoolLine(poolLine: ConfiguratorPoolLine) {
  configuratorCarouselElement.value?.slideToPoolLine(poolLine)
}

const formOpen = ref(false)
</script>

<template>
  <ClientOnly>
    <div class="flex flex-col gap-6 lg:flex-1 lg:overflow-auto">
      <ConfiguratorCarousel
        ref="configuratorCarouselElement"

        v-model:pool-line="currentPoolLine"
        class="flex flex-none lg:flex-1 lg:overflow-auto"
        :form-label="dt(props.configurator.translations)?.label ?? ''"
        :pool-lines="poolLines"
        :current-pool="currentPool"
        :background-image-day="configurator.backgroundImageDay"
        :background-image-night="configurator.backgroundImageNight"
        @open-form="formOpen = true"
      />
      <Transition name="fade" mode="out-in">
        <ConfiguratorSelectors
          :id="currentPoolLine"

          v-model:current-size="currentSize"
          v-model:current-stair="currentStair"
          v-model:current-color="currentColor"

          :current-shown-colors="currentShownColors"
          :current-selectable-colors="currentSelectableColors"
          :current-shown-sizes="currentShownSizes"
          :current-selectable-sizes="currentSelectableSizes"
          :current-shown-stairs="currentShownStairs"
          :current-selectable-stairs="currentSelectableStairs"

          :current-pool="currentPool"

          :pool-lines="poolLines"
          :current-pool-line="currentPoolLine"
          :is-fully-filled-in="isFullyFilledIn"

          :form-label="dt(props.configurator.translations)?.label ?? ''"
          @open-form="formOpen = true"

          @select-pool-line="onSelectPoolLine"
        />
      </Transition>
      <ConfiguratorFormDialog
        v-model:open="formOpen"
        :form="props.configurator.form"
        :title="dt(props.configurator.translations)?.form_title ?? ''"
        :description="dt(props.configurator.translations)?.form_description ?? ''"
        :current-pool="currentPool"
      />
    </div>
  </ClientOnly>
</template>
